<template>
  <div class="filtersSlider">
    <h5 class="filtersSlider__title">
      <span>{{ title }}</span>
    </h5>
    <div class="filtersSlider__value">
      <span>{{ timeRange.earliest }}</span>
      <span>{{ timeRange.latest }}</span>
    </div>
    <div class="sliderWrapper sliderWrapper--filter">
      <BaseSlider
        :model-value="sliderValues"
        :config="sliderConfig"
        @slide="handleSliderSlide"
        @change="handleSliderChange"
      />
    </div>
  </div>
  <!-- Buttons -->
  <FiltersItemTimeSliderButtons
    :unique-times="uniqueTimes"
    :current-indexes="sliderValues"
    :max-position="twentyFourHourCount"
    @update:filterObject="handleSliderChange"
  />
</template>

<script setup>
import { dateFormat } from '@js/utils/dateFns';
import { l } from '@js/utils/toolbox';
import { useFiltersStore } from './store/filtersStore';
import { useResultQueryStore } from './store/resultQueryStore';

// ============================================================================
// Props & Stores
// ============================================================================

const props = defineProps({
	title: {
		type: String,
		required: true
	},
	propertyKey: {
		type: String,
		required: true
	},
	tripIndex: {
		type: Number,
		required: true
	},
	uniqueTimes: {
		type: Array,
		required: true
	},
	type: {
		type: String,
		required: true
	}
});

const filtersStore = useFiltersStore();
const resultQueryStore = useResultQueryStore();

// ============================================================================
// Constants
// ============================================================================

const TWENTY_FOUR_HOURS_IN_SECONDS = 24 * 60 * 60;
const dateFormatUnits = props.propertyKey === 'departureTime' ? 'HH:mm' : 'EEE HH:mm';

// ============================================================================
// Computed Properties
// ============================================================================

/**
 * Calculate how many timestamps fall within the first 24 hours
 * Used to limit the slider range to relevant times
 */
const twentyFourHourCount = computed(() => {
	const firstTimestamp = props.uniqueTimes[0];
	const cutoffTime = firstTimestamp + TWENTY_FOUR_HOURS_IN_SECONDS;

	const index = props.uniqueTimes.findIndex(timestamp => timestamp > cutoffTime);

	// If all timestamps are within 24 hours, return number of timestamps, otherwise return first index beyond cutoff
	return index === -1 ? props.uniqueTimes.length - 1 : index;
});

/**
 * Slider configuration for nouislider
 */
const sliderConfig = computed(() => ({
	connect: [false, true, false],
	range: {
		min: [0],
		max: [twentyFourHourCount.value]
	},
	handleAttributes: [
		{ 'aria-label': `Min ${props.title}` },
		{ 'aria-label': `Max ${props.title}` }
	]
}));

/**
 * Get offset timestamps for the current trip and type
 */
const uniqueTimesOffset = computed(() => {
	return resultQueryStore.state.response.uniqueTimesOffset[props.tripIndex][props.type];
});

/**
 * Get current filter for this trip
 */
const currentFilter = computed(() => {
	const filters = filtersStore.state[props.propertyKey] || [];
	return filters.find(item => item.tripIndex === props.tripIndex);
});

/**
 * Convert timestamp position to formatted time text
 */
const getTimeText = (position) => {
	const isMaxPosition = position === twentyFourHourCount.value;
	const timestampIndex = isMaxPosition ? props.uniqueTimes.length - 1 : position;

	if (props.type === 'arrivals' && isMaxPosition) {
		return l('showresult.filter.arrival.max');
	}

	// Use the offset (seconds since midnight) directly instead of a timestamp
	const offsetSeconds = uniqueTimesOffset.value[timestampIndex];
	let hours = Math.floor(offsetSeconds / 3600);

	hours = hours % 24;

	const minutes = Math.floor((offsetSeconds % 3600) / 60);

	if (props.propertyKey === 'departureTime') {
		return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
	} else {
		// For arrival, you need the day name — derive it from the base midnight timestamp
		const midnightTimestamp = props.uniqueTimes[0] - uniqueTimesOffset.value[0];
		const localDate = new Date((midnightTimestamp + offsetSeconds) * 1000);
		const dayName = dateFormat(localDate, 'EEE'); // still TZ-sensitive, but only for day name
		return `${dayName} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
	}
};
// Check that both start and end exist in uniqueTimesOffset, if not insert into both uniqueTimesOffset and uniqueTimes
function sanityCheck () {
	// Skip sanity check if no current filter or no unique times available
	if (!currentFilter.value || !uniqueTimesOffset.value?.length || !props.uniqueTimes?.length) {
		return;
	}

	const offsets = [...uniqueTimesOffset.value];
	const times = [...props.uniqueTimes];

	const { start, end } = currentFilter.value;
	let didModify = false;

	// Calculate midnight once by subtracting first offset from first time
	const midnightTimestamp = times[0] - offsets[0];

	// Helper to insert a time at the correct sorted position in both arrays
	function insertTime (offsetValue) {
		if (offsets.includes(offsetValue)) {
			return;
		}

		console.debug(`[FiltersItemTimeSlider]: Inserting missing offset ${offsetValue} for tripIndex ${props.tripIndex}, type ${props.type}`);

		const insertIndex = offsets.findIndex(t => t > offsetValue);
		const actualIndex = insertIndex === -1 ? offsets.length : insertIndex;
		const fullTimestamp = midnightTimestamp + offsetValue;

		offsets.splice(actualIndex, 0, offsetValue);
		times.splice(actualIndex, 0, fullTimestamp);


		didModify = true;
	}

	insertTime(start);
	insertTime(end);


	if (didModify) {
		resultQueryStore.state.response.uniqueTimesOffset[props.tripIndex][props.type] = offsets;
		resultQueryStore.state.response.uniqueTimes[props.tripIndex][props.type] = times;
	}
}

sanityCheck();

/**
 * Two-way binding for slider values
 * Maps between filter store values and slider positions
 */
const sliderValues = computed(() => {
	if (!currentFilter.value) {
		return [0, twentyFourHourCount.value];
	}

	const { start, end } = currentFilter.value;
	const startIndex = uniqueTimesOffset.value.indexOf(start);
	let endIndex = uniqueTimesOffset.value.indexOf(end);
	if (endIndex > twentyFourHourCount.value) {
		endIndex = twentyFourHourCount.value;
	}

	// If showing full range, return max position
	const isFullRange = startIndex === 0 && endIndex === uniqueTimesOffset.value.length - 1;

	return isFullRange
		? [0, twentyFourHourCount.value]
		: [startIndex, endIndex];
});

/**
 * Current time range text for display
 */
const timeRange = ref({
	earliest: getTimeText(sliderValues.value[0]),
	latest: getTimeText(sliderValues.value[1])
});


// ============================================================================
// Methods
// ============================================================================


/**
 * Update display when sliderValues changes (e.g., filter reset)
 */
watch(sliderValues, updateTimeDisplay);

/**
 * Update the time range display text
 */
function updateTimeDisplay (sliderValues) {
	const [startPos, endPos] = sliderValues;
	timeRange.value = {
		earliest: getTimeText(startPos),
		latest: getTimeText(endPos)
	};
}

/**
 * Update display while slider is being dragged
 */
function handleSliderSlide (sliderValues) {
	updateTimeDisplay(sliderValues);
}

/**
 * Handle slider release - updates both display and store
 */
function handleSliderChange (sliderValues) {
	updateTimeDisplay(sliderValues);
	updateFilterStore(sliderValues);
}


/**
 * Update the filter store with new values
 * This triggers network requests, so only call on final slider release
 */
function updateFilterStore (sliderValues) {
	const [startPos, endPos] = sliderValues;

	// Determine actual timestamp values
	const isMaxPosition = endPos === twentyFourHourCount.value;
	const endIndex = isMaxPosition ? props.uniqueTimes.length - 1 : endPos;

	const offsets = uniqueTimesOffset.value;
	const isFullRange = startPos === 0 && endIndex === props.uniqueTimes.length - 1;

	const filters = [...(filtersStore.state[props.propertyKey] || [])];
	const existingIndex = filters.findIndex(item => item.tripIndex === props.tripIndex);

	if (isFullRange) {
		// Remove filter if showing full range
		if (existingIndex !== -1) {
			filters.splice(existingIndex, 1);
		}
	} else {
		// Add or update filter
		const newFilter = {
			tripIndex: props.tripIndex,
			start: offsets[startPos],
			end: offsets[endIndex]
		};

		if (existingIndex !== -1) {
			filters[existingIndex] = newFilter;
		} else {
			filters.push(newFilter);
		}
	}

	filtersStore.set({
		key: props.propertyKey,
		value: filters
	});
}
</script>
